<script lang="ts" setup>
import type { SafeHtml } from '@@/bits/html_parser'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import OzTabsBase from '@@/library/v4/components/OzTabsBase.vue'
import OzTooltip from '@@/library/v4/components/OzTooltip.vue'
import type { VueCssClass } from '@@/types'
import { computed, ref, useId } from 'vue'

const {
  accessibilityRole = OzTabsWithSeparatorAccessibilityRole.Tabs,
  darkMode = 'auto',
  sizePreset = OzTabsWithSeparatorSizePreset.H42px,
  tabsContainerClasses,
  nonDisabledButtonClasses,
  selectedTextColorClasses,
  nonSelectedTextColorClasses,
  currentTabKey,
} = defineProps<{
  /**
   * Label corresponding to the tab list.
   */
  ariaLabel: string
  /**
   * Tabs to display.
   */
  tabs: OzTabsWithSeparatorButtonProps[]
  /**
   * Key used to detect the current tab.
   */
  currentTabKey?: string
  /**
   * Applies preset sizes/shapes.
   * They are named with pixel values for developer ergonomics -- those are the values you see in the mockups.
   * Technically, the pixel value could vary depending on the size of `rem`.
   */
  sizePreset?: OzTabsWithSeparatorSizePreset
  /**
   * Accommodates the elements and attributes according to the use.
   * If `Tabs`, the component will be structured according to the ARIA tab list specification.
   * If `Navigation`, the component will be structured according to the ARIA navigation specification.
   */
  accessibilityRole?: OzTabsWithSeparatorAccessibilityRole
  /**
   * Dark mode.
   */
  darkMode?: boolean | 'auto'
  /**
   * Classes applied to the tabs container.
   */
  tabsContainerClasses?: string | undefined
  /**
   * Classes applied to the non-disabled tab button.
   */
  nonDisabledButtonClasses?: string | undefined
  /**
   * Classes applied to the selected tab button.
   */
  selectedTextColorClasses?: VueCssClass | undefined
  /**
   * Classes applied to the non-selected tab button.
   */
  nonSelectedTextColorClasses?: VueCssClass | undefined
}>()

defineEmits<{
  (
    e: 'activate-tab',
    event: Event,
    tab: {
      key: string
    },
  ): void
}>()

const tabButtonElsRef = ref<InstanceType<typeof OzPlainButton>[]>([])
const componentInstanceId = useId()

/** Selected tab text underline classes */
/**
 * Instead of using text-decoration-line: underline, we use a pseudo-element to style the underline.
 * This is because there are instances where the text within a tab is made up of multiple elements,
 * causing the underline to be broken if we use text-decoration-line: underline.
 */
const highContrastSelectedTabUnderlineClasses = computed(() => [
  'relative',
  "high-contrast:after:content-['']",
  'high-contrast:after:absolute',
  'high-contrast:after:h-px',
  'high-contrast:after:mx-3',
  'high-contrast:after:inset-x-0',
  'high-contrast:after:bottom-1',
  {
    'high-contrast:after:bg-grape-500': darkMode === false,
    'high-contrast:after:bg-canary-500': darkMode === true,
    'high-contrast:after:bg-grape-500 dark:high-contrast:after:bg-canary-500': darkMode === 'auto',
  },
])
</script>

<script lang="ts">
import { OzPlainButtonHrefMode as OzTabsWithSeparatorHrefMode } from '@@/library/v4/components/OzPlainButton.vue'
import {
  OzTabsBaseAccessibilityRole as OzTabsWithSeparatorAccessibilityRole,
  type OzTabsBaseTab,
} from '@@/library/v4/components/OzTabsBase.vue'

interface OzTabsWithSeparatorButtonProps extends OzTabsBaseTab {
  text: string
  hrefMode?: OzTabsWithSeparatorHrefMode
  iconName?: string
  colorPreset?: OzTabsWithSeparatorButtonColorPreset
  tooltipText?: string
  htmlTooltipText?: SafeHtml
  xTitleTooltip?: boolean
  startAdornmentText?: string
}

enum OzTabsWithSeparatorSizePreset {
  H28px = 'H28px',
  H37px = 'H37px',
  H40px = 'H40px',
  H42px = 'H42px',
  H48px = 'H48px',
  H59px = 'H59px',
}

enum OzTabsWithSeparatorButtonColorPreset {
  Primary = 'Primary',
}

export {
  OzTabsWithSeparatorAccessibilityRole,
  OzTabsWithSeparatorButtonColorPreset,
  OzTabsWithSeparatorHrefMode,
  OzTabsWithSeparatorSizePreset,
}
export type { OzTabsWithSeparatorButtonProps }
export default {}
</script>

<template>
  <OzTabsBase
    :tabs="tabs"
    :tab-list-aria-label="ariaLabel"
    :current-tab-key="currentTabKey"
    :accessibility-role="accessibilityRole"
    :tabs-container-classes="[tabsContainerClasses ?? '', 'items-center']"
    @activate-tab="(event, tab) => $emit('activate-tab', event, tab)"
  >
    <template #tabs="{ tabs: displayedTabs, currentTabId, setTabRef, computeTabAttributes, listItemRole, activateTab }">
      <template v-for="(tab, index) in displayedTabs as OzTabsWithSeparatorButtonProps[]" :key="tab.key">
        <li :ref="(el) => setTabRef(el, index, tab)" :role="listItemRole">
          <OzPlainButton
            ref="tabButtonElsRef"
            :data-testid="tab.testId"
            :class="[
              'flex items-center group',
              'outline-hidden',
              sizePreset == OzTabsWithSeparatorSizePreset.H28px && [
                'h-[1.75rem]',
                'rounded-[10px]',
                'px-3',
                'text-body-small',
              ],
              sizePreset == OzTabsWithSeparatorSizePreset.H37px && [
                'h-[2.3125rem]',
                'rounded-xl',
                'px-2',
                'text-body-small',
              ],
              sizePreset == OzTabsWithSeparatorSizePreset.H40px && ['h-10', 'rounded-2xl', 'px-2', 'text-heading-4'],
              sizePreset == OzTabsWithSeparatorSizePreset.H42px && [
                'h-[2.625rem]',
                'rounded-2xl',
                'px-2',
                'text-heading-3',
              ],
              sizePreset == OzTabsWithSeparatorSizePreset.H48px && ['h-12', 'rounded-2xl', 'px-2', 'text-heading-2'],
              sizePreset == OzTabsWithSeparatorSizePreset.H59px && [
                'h-[3.6875rem]',
                'rounded-2.5xl',
                'px-3',
                'text-heading-1',
              ],
              // Font
              'font-semibold',
              // Base
              currentTabId === tab.key && [
                highContrastSelectedTabUnderlineClasses,
                selectedTextColorClasses || {
                  'text-grape-500': darkMode === false,
                  'text-canary-500': darkMode === true,
                  'text-grape-500 dark:text-canary-500': darkMode === 'auto',
                },
              ],
              currentTabId !== tab.key && [
                nonSelectedTextColorClasses || [
                  tab.colorPreset == null && {
                    'text-dark-text-200': darkMode === false,
                    'text-light-text-200': darkMode === true,
                    'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
                  },
                  tab.colorPreset === OzTabsWithSeparatorButtonColorPreset.Primary && {
                    'text-padlet-pink': !tab.disabled,
                    'text-padlet-pink-100!': tab.disabled,
                  },
                ],
              ],
              // Hovered
              !tab.disabled && {
                'hhover:text-dark-text-100': darkMode === false,
                'hhover:text-light-text-100': darkMode === true,
                'hhover:text-dark-text-100 dark:hhover:text-light-text-100': darkMode === 'auto',
              },
              // Focused
              'ring-inset',
              sizePreset == OzTabsWithSeparatorSizePreset.H28px && 'focus-visible:ring-2',
              sizePreset == OzTabsWithSeparatorSizePreset.H37px && 'focus-visible:ring-2',
              sizePreset == OzTabsWithSeparatorSizePreset.H40px && 'focus-visible:ring-[2.5px]',
              sizePreset == OzTabsWithSeparatorSizePreset.H42px && 'focus-visible:ring-[2.5px]',
              sizePreset == OzTabsWithSeparatorSizePreset.H48px && 'focus-visible:ring-[2.5px]',
              sizePreset == OzTabsWithSeparatorSizePreset.H59px && 'focus-visible:ring-4',
              {
                'focus-visible:ring-grape-500': darkMode === false,
                'focus-visible:ring-canary-500': darkMode === true,
                'focus-visible:ring-grape-500 dark:focus-visible:ring-canary-500': darkMode === 'auto',
              },
              // Active
              !tab.disabled && {
                'hactive:text-grape-500': darkMode === false,
                'hactive:text-canary-500': darkMode === true,
                'hactive:text-grape-500 hactive:dark:text-canary-500': darkMode === 'auto',
              },
              // Custom classes
              !tab.disabled && nonDisabledButtonClasses,
            ]"
            v-bind="computeTabAttributes(tab)"
            :href="tab.href"
            :href-mode="tab.hrefMode"
            :size-preset="OzPlainButtonSizePreset.Bare"
            :color-scheme="OzPlainButtonColorScheme.Bare"
            :dark-mode="darkMode"
            :disabled="tab.disabled"
            :x-title="tab.xTitleTooltip"
            :aria-labelledby="`tab-text-${tab.key}`"
            :aria-describedby="
              tab.xTitleTooltip === false ? `custom-tooltip-${componentInstanceId}-${index}` : undefined
            "
            @click="activateTab($event, { key: tab.key })"
          >
            <div class="flex-1 truncate align-middle">
              <span v-if="tab.startAdornmentText" class="me-0.75" :aria-hidden="true">{{ tab.startAdornmentText }}</span
              ><span :id="`tab-text-${tab.key}`">{{ tab.text }}</span>
            </div>
            <OzIcon v-if="tab.iconName" class="ms-1" :name="tab.iconName" :size="12" />
            <template v-if="tab.xTitleTooltip === false">
              <span
                v-if="tab.htmlTooltipText"
                v-safe-html="tab.htmlTooltipText.toString()"
                :id="`custom-tooltip-${componentInstanceId}-${index}`"
                class="sr-only"
              />
              <span v-else :id="`custom-tooltip-${componentInstanceId}-${index}`" class="sr-only">
                {{ tab.tooltipText }}
              </span>
            </template>
            <OzTooltip
              v-if="tab.htmlTooltipText && tabButtonElsRef[index]"
              class="hidden hover-hover:group-hover:block w-max z-10"
              :anchor="tabButtonElsRef[index].$el"
              :config="{
                placement: 'bottom-start',
              }"
            >
              <p v-safe-html="tab.htmlTooltipText.toString()" />
            </OzTooltip>
            <OzTooltip
              v-else-if="tab.tooltipText && tabButtonElsRef[index]"
              class="hidden hover-hover:group-hover:block w-max z-10"
              :anchor="tabButtonElsRef[index].$el"
              :config="{
                placement: 'bottom-start',
              }"
            >
              {{ tab.tooltipText }}
            </OzTooltip>
          </OzPlainButton>
        </li>
        <li
          v-if="index !== displayedTabs.length - 1"
          :key="`${tab.key}-divider`"
          :class="[
            sizePreset == OzTabsWithSeparatorSizePreset.H28px && 'text-body',
            sizePreset == OzTabsWithSeparatorSizePreset.H37px && 'text-body',
            sizePreset == OzTabsWithSeparatorSizePreset.H40px && 'text-body',
            sizePreset == OzTabsWithSeparatorSizePreset.H42px && 'text-body-large',
            sizePreset == OzTabsWithSeparatorSizePreset.H48px && 'text-body-large',
            sizePreset == OzTabsWithSeparatorSizePreset.H59px && 'text-heading-1',
            'select-none',
            {
              'text-dark-text-400': darkMode === false,
              'text-light-text-400': darkMode === true,
              'text-dark-text-400 dark:text-light-text-400': darkMode === 'auto',
            },
          ]"
          aria-hidden="true"
          tabindex="-1"
        >
          /
        </li>
      </template>
    </template>
  </OzTabsBase>
</template>
